import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import UserContext from './UserContext';
import ReviewSubmissionInvalidForm from './ReviewSubmissionInvalidForm';
import ReviewSubmissionValidForm from './ReviewSubmissionValidForm';
import { toast } from './toast';
import { Box, Button, ButtonGroup, Typography, Container, CircularProgress, TextField } from '@mui/material';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const CropOverlay = ({ cropRegion, setCropRegion, containerRef, videoRef }) => {
  const dragRef = useRef(null);
  const didDragRef = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      didDragRef.current = true;
      const { action, startX, startY, startCrop: sc, rect } = dragRef.current;
      const dx = (e.clientX - startX) / rect.width;
      const dy = (e.clientY - startY) / rect.height;
      let { x, y, w, h } = sc;
      const MIN = 0.05;

      if (action === 'move') {
        x = clamp(x + dx, 0, 1 - w);
        y = clamp(y + dy, 0, 1 - h);
      } else if (action === 'tl') {
        const nx = clamp(x + dx, 0, x + w - MIN);
        const ny = clamp(y + dy, 0, y + h - MIN);
        w += x - nx; h += y - ny; x = nx; y = ny;
      } else if (action === 't') {
        const ny = clamp(y + dy, 0, y + h - MIN);
        h += y - ny; y = ny;
      } else if (action === 'tr') {
        const ny = clamp(y + dy, 0, y + h - MIN);
        h += y - ny; y = ny;
        w = clamp(w + dx, MIN, 1 - x);
      } else if (action === 'r') {
        w = clamp(w + dx, MIN, 1 - x);
      } else if (action === 'br') {
        w = clamp(w + dx, MIN, 1 - x);
        h = clamp(h + dy, MIN, 1 - y);
      } else if (action === 'b') {
        h = clamp(h + dy, MIN, 1 - y);
      } else if (action === 'bl') {
        const nx = clamp(x + dx, 0, x + w - MIN);
        w += x - nx; x = nx;
        h = clamp(h + dy, MIN, 1 - y);
      } else if (action === 'l') {
        const nx = clamp(x + dx, 0, x + w - MIN);
        w += x - nx; x = nx;
      }
      setCropRegion({ x, y, w, h });
    };

    const onUp = () => { dragRef.current = null; };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [setCropRegion]);

  const startDrag = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      action,
      startX: e.clientX,
      startY: e.clientY,
      startCrop: { ...cropRegion },
      rect: containerRef.current.getBoundingClientRect(),
    };
  };

  const handleClick = () => {
    if (didDragRef.current) { didDragRef.current = false; return; }
    const video = videoRef.current;
    if (!video) return;
    video.paused ? video.play() : video.pause();
  };

  const { x, y, w, h } = cropRegion;
  const p = (v) => `${v * 100}%`;
  const HS = 10;
  const dark = 'rgba(0,0,0,0.45)';

  const handles = [
    { id: 'tl', cx: x,       cy: y,       cur: 'nw-resize' },
    { id: 't',  cx: x + w/2, cy: y,       cur: 'n-resize'  },
    { id: 'tr', cx: x + w,   cy: y,       cur: 'ne-resize' },
    { id: 'r',  cx: x + w,   cy: y + h/2, cur: 'e-resize'  },
    { id: 'br', cx: x + w,   cy: y + h,   cur: 'se-resize' },
    { id: 'b',  cx: x + w/2, cy: y + h,   cur: 's-resize'  },
    { id: 'bl', cx: x,       cy: y + h,   cur: 'sw-resize' },
    { id: 'l',  cx: x,       cy: y + h/2, cur: 'w-resize'  },
  ];

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: p(y), background: dark }} />
      <div style={{ position: 'absolute', top: p(y + h), left: 0, right: 0, bottom: 0, background: dark }} />
      <div style={{ position: 'absolute', top: p(y), left: 0, width: p(x), height: p(h), background: dark }} />
      <div style={{ position: 'absolute', top: p(y), left: p(x + w), right: 0, height: p(h), background: dark }} />
      <div
        style={{
          position: 'absolute',
          top: p(y), left: p(x),
          width: p(w), height: p(h),
          border: '2px dashed white',
          boxSizing: 'border-box',
          cursor: 'grab',
          pointerEvents: 'all',
        }}
        onClick={handleClick}
        onMouseDown={(e) => startDrag(e, 'move')}
      />
      {handles.map(({ id, cx, cy, cur }) => (
        <div
          key={id}
          style={{
            position: 'absolute',
            left: `calc(${p(cx)} - ${HS / 2}px)`,
            top: `calc(${p(cy)} - ${HS / 2}px)`,
            width: HS, height: HS,
            background: 'white',
            border: '1px solid rgba(0,0,0,0.5)',
            cursor: cur,
            pointerEvents: 'all',
          }}
          onMouseDown={(e) => startDrag(e, id)}
        />
      ))}
    </div>
  );
};

const ReviewSubmission = () => {
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const { db, storage, auth } = useContext(UserContext);
  const [submission, setSubmission] = useState(null);
  const { submissionId } = useParams();
  const [invalidReason, setInvalidReason] = useState('');
  const [videoAngle, setVideoAngle] = useState('');
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isVideoFullWidth, setIsVideoFullWidth] = useState(true);
  const [cropRegion, setCropRegion] = useState({ x: 0, y: 0, w: 1, h: 0.9 });
  const [highlights, setHighlights] = useState([{ text: '', isGood: true }]);
  const [tips, setTips] = useState([{ title: '', description: '' }]);
  const [symptomsSummary, setSymptomsSummary] = useState('');
  const [isSprinter, setIsSprinter] = useState(false);
  const [timestampImages, setTimestampImages] = useState({
    side: {
      LR: '',
      MSt: '',
      LSt: '',
      MSw: '',
    },
    back: {
      MSt: { left: '', right: '' },
      IC: { left: '', right: '' },
      MSw: { left: '', right: '' },
    },
  });

  const [timestamps, setTimestamps] = useState({
    side: { LR: null, MSt: null, LSt: null, MSw: null },
    back: {
      MSt: { left: null, right: null },
      IC: { left: null, right: null },
      MSw: { left: null, right: null },
    },
  });

  const [processingDone, setProcessingDone] = useState(false);
  const [poseLandmarks, setPoseLandmarks] = useState(null);  // {phase: {name: {x,y,v}, _stance, _direction}}
  const [pipelineTiming, setPipelineTiming] = useState(null);

  // Reset processing status if timestamps change
  useEffect(() => {
    setProcessingDone(false);
  }, [timestamps]);

  const handleProcessingSuccess = async (images, metrics, landmarks, timing) => {
    try {
      const updatedImages = {};
      // Fetch authorized download URLs for each processed image path
      for (const [key, url] of Object.entries(images)) {
        // The URL returned is currently a raw one, we need to extract the blob path
        // URL format: https://firebasestorage.googleapis.com/v0/b/.../o/videos%2FUID%2FFILENAME_PHASE?alt=media
        const pathEncoded = url.split('/o/')[1].split('?')[0];
        const path = decodeURIComponent(pathEncoded);
        const authorizedUrl = await getDownloadURL(ref(storage, path));
        updatedImages[key] = authorizedUrl;
      }

      setTimestampImages(prev => {
        const updated = { ...prev };
        if (videoAngle === 'side') {
          updated.side = { ...prev.side, ...updatedImages };
        } else {
          // Handle back keys: MSt -> updated.back.MSt.left, MSt_right -> updated.back.MSt.right
          Object.entries(updatedImages).forEach(([key, url]) => {
            if (key.includes('_')) {
              const [phase, leg] = key.split('_');
              if (updated.back[phase]) {
                updated.back[phase] = { ...updated.back[phase], [leg]: url };
              }
            } else {
              // Default to 'left' if no suffix (matches backend logic)
              if (updated.back[key]) {
                updated.back[key] = { ...updated.back[key], left: url };
              }
            }
          });
        }
        return updated;
      });

      // Pre-fill side-view metrics from pose estimation
      if (metrics && videoAngle === 'side') {
        setData(prev => ({
          ...prev,
          side: {
            ...prev.side,
            LR: {
              ...prev.side.LR,
              ...(metrics.LR?.footStrikeAngle      !== undefined && { footStrikeAngle:      metrics.LR.footStrikeAngle }),
              ...(metrics.LR?.tibiaAngle           !== undefined && { tibiaAngle:           metrics.LR.tibiaAngle }),
              ...(metrics.LR?.degreeOfOverstriding !== undefined && { degreeOfOverstriding: metrics.LR.degreeOfOverstriding }),
            },
            MSt: {
              ...prev.side.MSt,
              ...(metrics.MSt?.kneeFlexionAngle !== undefined && { kneeFlexionAngle: metrics.MSt.kneeFlexionAngle }),
              ...(metrics.MSt?.heelHeight       !== undefined && { heelHeight:       metrics.MSt.heelHeight }),
            },
            LSt: {
              ...prev.side.LSt,
              ...(metrics.LSt?.trunkLeanAngle    !== undefined && { trunkLeanAngle:    metrics.LSt.trunkLeanAngle }),
              ...(metrics.LSt?.hipExtensionAngle !== undefined && { hipExtensionAngle: metrics.LSt.hipExtensionAngle }),
            },
          },
        }));
        toast.info("Metrics pre-filled from pose estimation — please verify.");
      }

      if (landmarks) setPoseLandmarks(landmarks);
      if (timing) setPipelineTiming(timing);

      setProcessingDone(true);
    } catch (error) {
      console.error('Error fetching authorized URLs:', error);
      toast.error("Error displaying processed frames.");
    }
  };

  const [data, setData] = useState({
    side: {
      LR: {
        footStrikeAngle: undefined,
        tibiaAngle: undefined,
        degreeOfOverstriding: undefined,
      },
      MSt: {
        kneeFlexionAngle: undefined,
        heelHeight: undefined,
        verticalDisplacement: undefined,
      },
      LSt: {
        trunkLeanAngle: undefined,
        hipExtensionAngle: undefined,
      }
    },
    back: {
      IC: {
        pelvicDrop: { left: undefined, right: undefined },
        heelEversion: { left: undefined, right: undefined },
      },
      MSt: {
        baseOfSupport: undefined,
        kneeWindow: undefined,
        toeOutAngle: { left: undefined, right: undefined },
        heelHeight: { left: undefined, right: undefined },
        verticalDisplacement: { left: undefined, right: undefined },
      },
      MSw: {
        heelWhipAngle: { left: undefined, right: undefined },
      },
    },
    cadence: undefined,
    treadmillShakingDegree: undefined,
  });

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Forma | Review Submission";

    const fetchSubmissionData = async () => {
      try {
        const submissionDocRef = doc(db, 'submissions', submissionId);
        const submissionDocSnapshot = await getDoc(submissionDocRef);

        if (submissionDocSnapshot.exists()) {
          setSubmission({ id: submissionDocSnapshot.id, ...submissionDocSnapshot.data() });
        } else {
          console.error('Submission not found:', submissionId);
        }
      } catch (error) {
        console.error('Error fetching submission:', error);
        toast.error('Error fetching submission. Please retry.', { toastId: 'reviewError' });
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissionData();
  }, [db, submissionId]);

  const pendingSeekRef = useRef(0);

  useEffect(() => {
    const applyPending = () => {
      const video = videoRef.current;
      if (!video || pendingSeekRef.current === 0) return;
      video.currentTime = Math.max(0, Math.min(video.duration || 0, video.currentTime + pendingSeekRef.current));
      pendingSeekRef.current = 0;
    };

    const handleKeydown = (e) => {
      const video = videoRef.current;
      if (!video) return;
      const amount = e.shiftKey ? 0.5 : 0.01;
      if (e.keyCode === 37) pendingSeekRef.current -= amount;
      else if (e.keyCode === 39) pendingSeekRef.current += amount;
      else return;
      if (!video.seeking) applyPending();
    };

    const handleSeeked = () => applyPending();

    window.addEventListener('keydown', handleKeydown);
    videoRef.current?.addEventListener('seeked', handleSeeked);
    const vid = videoRef.current;
    return () => {
      window.removeEventListener('keydown', handleKeydown);
      vid?.removeEventListener('seeked', handleSeeked);
    };
  }, []);

  const handleInvalid = async (event) => {
    event.preventDefault();
    try {
       await updateDoc(doc(db, 'submissions', submissionId), {
         status: 'invalid',
         feedback: invalidReason,
         symptomsSummary: symptomsSummary ? symptomsSummary : null,
       });
       toast.success("Submission has been marked as invalid.");
       navigate('/admin');
     } catch (error) {
       console.error('Error updating document:', error);
       toast.error("Error updating document. Please try again.");
     }
  };

  const handleValid = async (event) => {
    event.preventDefault();
    try {
      setIsUploading(true);

      // No longer need to upload images here as they are uploaded by the Python function
      await updateDoc(doc(db, 'submissions', submissionId), {
         status: 'reviewed',
         videoAngle: videoAngle,
         isSprinter: isSprinter,
         symptomsSummary: symptomsSummary ? symptomsSummary : null,
         results: {
           sideViewData: videoAngle === 'side' ? data.side : null,
           backViewData: videoAngle === 'back' ? data.back : null,
           cadence: data.cadence,
           treadmillShakingDegree: data.treadmillShakingDegree || null,
           highlights: highlights,
           tips: tips
         },
       });
      toast.success("Submission has been marked as reviewed.");
      navigate(`/admin/user/${submission.userId}/results/${submissionId}`);
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error("Error updating document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handlePlaybackRate = (rate) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" align="center" gutterBottom>
        Review Submission
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      ) : !submission ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography variant="h6">No submission data found.</Typography>
        </Box>
      ) : (
        <>
          <Box mb={1}>
            <div
              ref={videoContainerRef}
              style={{
                position: 'relative',
                display: 'inline-block',
                width: isVideoFullWidth ? "100%" : "50%",
                transition: "width 0.3s ease",
              }}
            >
              <video
                crossOrigin="anonymous"
                controls
                loop
                ref={videoRef}
                style={{ width: "100%", display: "block" }}
              >
                <source src={submission.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <CropOverlay
                cropRegion={cropRegion}
                setCropRegion={setCropRegion}
                containerRef={videoContainerRef}
                videoRef={videoRef}
              />
            </div>
            <Box display="flex" justifyContent="start" mt={1} alignItems="center" flexWrap="wrap" gap={1}>
              <Button variant="contained" size="small" onClick={() => setIsVideoFullWidth(!isVideoFullWidth)}>
                {isVideoFullWidth ? "Shrink to 50%" : "Expand to Full Width"}
              </Button>
              <ButtonGroup size="small" variant="contained" sx={{ ml: 2 }}>
                <Button onClick={() => handlePlaybackRate(1)}>1x</Button>
                <Button onClick={() => handlePlaybackRate(2)}>2x</Button>
                <Button onClick={() => handlePlaybackRate(3)}>3x</Button>
                <Button onClick={() => handlePlaybackRate(4)}>4x</Button>
                <Button onClick={() => handlePlaybackRate(8)}>8x</Button>
              </ButtonGroup>
            </Box>
          </Box>
          <Typography variant="body1">
            User ID: <a href={`/admin/user/${submission.userId}/results`} style={{ color: 'inherit' }}>{submission.userId}</a>
          </Typography>
          <Typography variant="body1">
            Video Name: {decodeURIComponent(submission.videoUrl.split('/o/')[1].split('?')[0]).split('/').pop()}
          </Typography>
          <Typography variant="body1">Timestamp: {new Date(submission.timestamp?.seconds * 1000).toLocaleString()}</Typography>
          <Typography variant="body1">Goal: {submission.goal}</Typography>
          <Typography variant="body1">Status: {submission.status}</Typography>
          <Box display="flex" alignItems="center">
            <Typography variant="body1" mr={2}>
              Symptoms: {submission.symptomsText ? submission.symptomsText : 'None'}
            </Typography>
            <TextField
              label="Summarize, e.g. 'Left knee'"
              variant="outlined"
              size="small"
              value={symptomsSummary}
              onChange={(e) => setSymptomsSummary(e.target.value)}
              disabled={!submission.symptomsText}
              style={{ flexGrow: 1 }}
            />
          </Box>
          <Box mt={4}>
            <Typography variant="h5">Admin Review</Typography>
            <ReviewSubmissionInvalidForm
              invalidReason={invalidReason}
              setInvalidReason={setInvalidReason}
              handleInvalid={handleInvalid}
            />
            <ReviewSubmissionValidForm
              submissionId={submissionId}
              userId={submission.userId}
              videoRef={videoRef}
              cropRegion={cropRegion}
              videoAngle={videoAngle}
              setVideoAngle={setVideoAngle}
              timestampImages={timestampImages}
              setTimestampImages={setTimestampImages}
              timestamps={timestamps}
              setTimestamps={setTimestamps}
              onProcessingSuccess={handleProcessingSuccess}
              processingDone={processingDone}
              data={data}
              setData={setData}
              handleValid={handleValid}
              highlights={highlights}
              setHighlights={setHighlights}
              tips={tips}
              setTips={setTips}
              isUploading={isUploading}
              goal={submission.goal}
              symptoms={symptomsSummary || submission.symptomsText}
              isSprinter={isSprinter}
              setIsSprinter={setIsSprinter}
              poseLandmarks={poseLandmarks}
              pipelineTiming={pipelineTiming}
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default ReviewSubmission;
